import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { usePSNavigate } from '../../hooks/usePSNavigate'
import { Industry } from '../../models/Industry'
import { RecordFactory } from '../../models/RecordFactory'
import { isValidEmailAddress, isValidPhoneNumber, isValidUrl, isValidUsername } from '../../utils'
import { e164ToPhone } from '../../utils/phone'
import { ComponentData } from '../../utils/typing'
import { NavigationBlockerPrompt } from '../NavigationBlockerPrompt'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import Label from '../app/design/Label'
import { SearchableSelect } from '../common/fields/SearchableSelect'
import { TextArea } from '../common/fields/TextArea'
import { TextField } from '../common/fields/TextField'
import { WorkflowPage } from '../workflows/WorkflowPage'

const viewWillAppear = async (base: PocketComponent) => {
    const [industries, otherAccountUsers] = await Promise.all([
        RecordFactory.getInstance().loadIndustries(),
        RecordFactory.getInstance()
            .fetchRoles('switch')
            .then((result) => {
                if (!result.ok) {
                    return []
                }
                return result.value.filter(
                    (user) => (user.solo || user.company) && user.id !== base.me.id
                )
            }),
    ])

    return {
        user: base.me,
        record: base.me.clone(),
        industries,
        otherAccountUsers,
    }
}

const render = ({ data, base }: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    const [user, setUser] = useState(data.record)
    const navigate = usePSNavigate()
    const { logAndDisplayError } = useErrorHandler()
    const queryClient = useQueryClient()

    const [phoneNumber, setPhoneNumber] = useState(e164ToPhone(user.main?.slice(-10)) ?? '')
    const [photo, setPhoto] = useState<
        { type: 'file'; file: File } | { type: 'url'; url: string } | null
    >(user.image?.url ? { type: 'url', url: user.image.url.toString() } : null)

    const industryOptions = data.industries.map((industry) => ({
        id: industry.id ?? '',
        title: industry.name ?? '',
    }))

    const businessLineValidation =
        isValidPhoneNumber(phoneNumber) || (phoneNumber ?? '').length === 0 ? undefined : 'error'

    const isSuperAdmin = user.company || user.solo || user.partner

    const emailValid = user.email === data.user.email || !!isValidEmailAddress(user.email)
    const usernameValid = !user.username || isValidUsername(user.username)
    const websiteValid = !user.url || isValidUrl(user.url)

    const hasEdits =
        phoneNumber !== e164ToPhone(data.user.main?.slice(-10)) ||
        user.entity !== data.user.entity ||
        user.industry?.id !== data.user.industry?.id ||
        user.email !== data.user.email ||
        user.username !== data.user.username ||
        user.bio !== data.user.bio ||
        user.url !== data.user.url ||
        photo?.type === 'file'

    const isFormValid = emailValid && usernameValid && websiteValid

    const saveEnabled =
        hasEdits && isFormValid && (data.otherAccountUsers.length === 0 || !!user.entity)

    async function submitChanges() {
        user.main = phoneNumber.replace(/\D/g, '')
        base.showProgressBar('Saving..')

        const thumbNail = photo?.type === 'file' ? photo.file : undefined
        const result = await RecordFactory.getInstance().sendUser(user, thumbNail)

        base.hideProgressBar()

        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        queryClient.invalidateQueries({ queryKey: ['account-switcher-users'] })
        navigate('/settings')
    }

    return (
        <WorkflowPage
            title="Profile"
            footerContent={
                <div className="flex w-full items-center justify-end gap-x-4">
                    <Button intent="primary" disabled={!saveEnabled} onClick={submitChanges}>
                        Save
                    </Button>
                </div>
            }
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start gap-y-6">
                <WorkflowPage.Card>
                    <div className="form">
                        <div className="flex w-full flex-col items-center">
                            <WorkflowPage.PhotoPicker
                                onSelectPhoto={(photoFile) =>
                                    setPhoto({ type: 'file', file: photoFile })
                                }
                                selectedPhoto={photo}
                                onClearPhoto={() => setPhoto(null)}
                            />
                            <div className="text-label-sm mt-4 font-normal text-gray-600">
                                Your logo will appear on transactions and receipts sent to clients
                            </div>
                        </div>
                        <div className="mt-8 flex flex-col items-stretch justify-start gap-y-8">
                            <WorkflowPage.IconSection title="Business name" icon="briefcase.large">
                                <TextField
                                    size="medium"
                                    placeholder="Enter business name"
                                    value={user.entity}
                                    onChange={(event) =>
                                        setUser(user.clone({ entity: event.target.value }))
                                    }
                                    onClear={() => setUser(user.clone({ entity: '' }))}
                                />
                            </WorkflowPage.IconSection>

                            {!user.partner && (
                                <WorkflowPage.IconSection title="Industry" icon={'settings.large'}>
                                    <SearchableSelect
                                        size="medium"
                                        className="border-gray-500"
                                        items={industryOptions}
                                        onSelectItem={(item) => {
                                            const industry = data.industries.find(
                                                (industry: Industry) => industry.id === item?.id
                                            )
                                            if (industry) {
                                                setUser(user.clone({ industry }))
                                            }
                                        }}
                                        selectedItemID={user.industry?.id}
                                        shouldPreserveLastSelection
                                    />
                                </WorkflowPage.IconSection>
                            )}
                            {(user.solo || user.company) && (
                                <WorkflowPage.IconSection
                                    title="Business line"
                                    icon={'phone2.large'}
                                >
                                    <TextField
                                        size="medium"
                                        placeholder="Enter business line"
                                        mask="(000) 000-0000"
                                        value={phoneNumber}
                                        onChange={(event) => {
                                            setPhoneNumber(event.target.value)
                                        }}
                                        hasError={businessLineValidation === 'error'}
                                        onClear={() => setPhoneNumber('')}
                                    />
                                    {!!businessLineValidation && (
                                        <Label size="xs" intent="danger">
                                            Only US phone numbers are allowed.
                                        </Label>
                                    )}
                                </WorkflowPage.IconSection>
                            )}

                            <WorkflowPage.IconSection
                                title="Email (required)"
                                description="Email address will appear on transactions and receipts sent to clients"
                                icon={'envelope.large'}
                            >
                                <TextField
                                    size="medium"
                                    placeholder="Enter email address"
                                    value={user.email}
                                    onChange={(event) =>
                                        setUser(user.clone({ email: event.target.value }))
                                    }
                                    hasError={!emailValid}
                                    onClear={() => setUser(user.clone({ email: '' }))}
                                />
                                {!emailValid && (
                                    <Label size="sm" intent="danger" className="block pt-3">
                                        {!user.email
                                            ? 'Enter a valid email address'
                                            : 'That does not appear to be a valid email address.'}
                                    </Label>
                                )}
                            </WorkflowPage.IconSection>

                            {isSuperAdmin && (
                                <>
                                    <WorkflowPage.IconSection
                                        title="Website"
                                        description="Show your website on invoices, receipts, and your Suite Link"
                                        icon={'globe2.large'}
                                    >
                                        <TextField
                                            size="medium"
                                            placeholder="Enter website"
                                            value={user.url}
                                            onChange={(event) =>
                                                setUser(user.clone({ url: event.target.value }))
                                            }
                                            hasError={!websiteValid}
                                            onClear={() => setUser(user.clone({ url: '' }))}
                                        />
                                        {!websiteValid && (
                                            <Label size="sm" intent="danger">
                                                That does not appear to be a valid website address.
                                            </Label>
                                        )}
                                    </WorkflowPage.IconSection>

                                    <WorkflowPage.IconSection
                                        title="Username"
                                        description="Username will be the “User ID” for your Suite link and online booking pages"
                                        icon={'user2.large'}
                                    >
                                        <TextField
                                            size="medium"
                                            placeholder="Enter username"
                                            value={user.username}
                                            hasError={!usernameValid}
                                            onChange={(event) =>
                                                setUser(
                                                    user.clone({ username: event.target.value })
                                                )
                                            }
                                            onClear={() => setUser(user.clone({ username: '' }))}
                                        />
                                        {!usernameValid && (
                                            <Label size="sm" intent="danger">
                                                Limited to 49 characters & can only contain letters,
                                                numbers, dashes & underscores
                                            </Label>
                                        )}
                                    </WorkflowPage.IconSection>
                                </>
                            )}
                            {!isSuperAdmin && user.client && (
                                <WorkflowPage.IconSection title="Username" icon={'user2.large'}>
                                    <TextField
                                        size="medium"
                                        placeholder="Enter username"
                                        value={user.username}
                                        hasError={!usernameValid}
                                        onChange={(event) =>
                                            setUser(user.clone({ username: event.target.value }))
                                        }
                                        onClear={() => setUser(user.clone({ username: '' }))}
                                    />
                                    {!usernameValid && (
                                        <Label size="sm" intent="danger">
                                            Limited to 49 characters & can only contain letters,
                                            numbers, dashes & underscores
                                        </Label>
                                    )}
                                </WorkflowPage.IconSection>
                            )}

                            <WorkflowPage.IconSection
                                title={isSuperAdmin ? 'Description' : 'About yourself'}
                                description="Tell clients about your business when they view your booking site"
                                icon={'note.large'}
                                secondaryText={`${(user.bio ?? '').length} / 1200`}
                            >
                                <TextArea
                                    className="h-45"
                                    placeholder={
                                        isSuperAdmin ? 'Profile description' : 'Description'
                                    }
                                    value={user.bio}
                                    maxLength={1200}
                                    onChange={(event) =>
                                        setUser(user.clone({ bio: event.target.value }))
                                    }
                                    onClear={() => setUser(user.clone({ bio: '' }))}
                                />
                            </WorkflowPage.IconSection>
                        </div>
                    </div>
                </WorkflowPage.Card>
            </div>
            <NavigationBlockerPrompt when={hasEdits} />
        </WorkflowPage>
    )
}

export const SetupProfilePage = usePSComponent(render, viewWillAppear)
